import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, X, Trash2, CalendarCheck2, CalendarPlus, User, CheckCircle2, AlertTriangle, Music2, UserX, Search, Copy, ImagePlus, Download, Share2, ExternalLink, FileText, Pencil, Check } from "lucide-react";
import { apiFetch, apiPost, apiPatch, apiDelete } from "./api/client";
import { UI } from "./designSystem";
import { cifraUrlComTom } from "./musicKeys";

// Mesma normalizacao usada no backend (ver normalizeNameText em
// mosaico-api/src/routes/members.js) -- os pickers de "Escalar"/"Vago"/
// "Indisponivel"/repertorio filtram uma lista ja carregada no cliente, sem
// passar pelo backend, entao precisam da mesma logica aqui. Pedido do
// Rodrigo, 30/10/2026: "romulo" precisa achar "Rômulo" em todas as buscas.
function normalizeNameText(value) {
  if (!value) return "";
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

// Domingos entre hoje e o fim do mes corrente que AINDA NAO tem escala
// criada -- usado tanto pra desabilitar o botao "Gerar domingos do mes"
// quando nao sobrou nenhum (pedido do Drigo, 20/10/2026: "precisa ser
// ativado so' se ja n foi gerado, pra nao matar a escala ja montada") quanto
// pelo proprio handler, pra nao duplicar essa conta em dois lugares.
// Generalizacao do antigo getPendingSundaysThisMonth (so domingo, fixo) --
// agora aceita qualquer dia da semana (0=domingo...6=sabado), pra servir
// tanto o gerador de Musicos (domingo) quanto o de Zeladoria (sabado,
// pedido do Drigo 28/10/2026: equipe de zeladoria se reune aos sabados as
// 10h, nao domingo).
function getPendingWeekdaysThisMonth(escalas, weekday) {
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const existingDates = new Set((escalas || []).map((e) => String(e.date).slice(0, 10)));

  // Primeiro dia-alvo a partir de hoje (inclui hoje se hoje ja for o dia).
  const cursor = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const diasAteOAlvo = (7 + weekday - cursor.getUTCDay()) % 7;
  cursor.setUTCDate(cursor.getUTCDate() + diasAteOAlvo);

  // Janela ate o fim do MES SEGUINTE (nao so' o corrente) -- se so' fosse
  // ate o fim do mes corrente, perto do fim do mes (ex.: hoje 28/10, sem
  // mais nenhum domingo antes de 31/10) a janela dava vazia e o botao
  // ficava preso dizendo "ja gerado" mesmo sem nenhuma escala criada pro
  // mes seguinte (bug relatado pelo Drigo, 28/10/2026). Com a janela
  // sempre cobrindo tambem o mes seguinte inteiro, sempre sobra o que
  // gerar enquanto houver data futura sem escala.
  const windowEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 2, 0));

  const datas = [];
  while (cursor <= windowEnd) {
    const iso = cursor.toISOString().slice(0, 10);
    if (iso >= todayIso && !existingDates.has(iso)) datas.push(iso);
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return datas;
}

// Config do gerador semanal por tipo de escala -- weekday (0=domingo,
// 6=sabado), o rotulo padrao da vaga (ex.: horario, pra lembrar sem
// precisar abrir) e o texto em portugues (singular/plural) pros botoes e
// avisos. Tipo sem entrada aqui cai no padrao (domingo, igual sempre foi).
const WEEKLY_GENERATOR_CONFIG = {
  MUSICOS: { weekday: 0, defaultLabel: "", singular: "domingo", plural: "domingos" },
  ZELADORIA: { weekday: 6, defaultLabel: "10h", singular: "sábado", plural: "sábados" },
  // Devocional Teens: sempre as mesmas 2 vagas fixas (Meninos/Meninas) --
  // defaultSlots faz o gerador ja' criar as duas vazias, em vez de deixar
  // "Nenhuma vaga ainda" igual aconteceu com Zeladoria (ver correcao de
  // 28/10/2026 no script de import).
  TEENS_DEVOCIONAL: { weekday: 2, defaultLabel: "19h30", singular: "terça", plural: "terças", defaultSlots: ["Responsável Meninos", "Responsável Meninas"] }
};
const DEFAULT_WEEKLY_GENERATOR_CONFIG = { weekday: 0, defaultLabel: "", singular: "domingo", plural: "domingos" };
function getWeeklyGeneratorConfig(tipo) {
  return WEEKLY_GENERATOR_CONFIG[tipo] || DEFAULT_WEEKLY_GENERATOR_CONFIG;
}
import { getEffectiveRoles, getSimulatedIsMusician, getSimulatedMinistryFlag } from "./roleSimulator";

// Escala de musicos (equipe de louvor): uma data de culto/ensaio com vagas
// por funcao (ex.: "Violao", "Bateria"), cada vaga podendo ter um musico
// escalado. Interacao de toque simples -- clica na vaga, escolhe o musico
// numa lista, pronto -- decisao explicita do Drigo (17/10/2026) em vez de
// arrastar (drag-and-drop), pra ficar confiavel no celular tambem. ADMIN e
// MUSICO podem editar (mesmo gate do catalogo de Repertorio -- ver
// AdminRepertorio.jsx). Ver rotas em mosaico-api/src/routes/escalas.js.

const ADMIN_PATH = "/admin";

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

// Texto FIXO que routes/escalas.js (tryNotifySlot) devolve quando o motivo da falha e' o
// template da Meta ainda nao aprovado -- esse motivo afeta TODA vaga igual (nao e' um
// problema de uma pessoa especifica), entao esse caso NAO mostra o triangulo de alerta por
// pessoa (o aviso geral no topo da pagina ja cobre isso) -- so' um erro de verdade,
// especifico dela (ex.: sem WhatsApp cadastrado), ganha o destaque. Pedido do Drigo,
// 27/10/2026: "nao tem template, nao tem pra ninguem, nao faz sentido o destaque no nome
// das pessoas". Precisa bater exatamente com o texto em escalas.js.
const NOTIFY_ERROR_TEMPLATE_PENDENTE =
  "Aviso automático ainda pendente: aguardando aprovação do modelo de mensagem pela Meta (não é problema desta pessoa).";

// Vagas escaladas ANTES de 27/10/2026 (correção acima) guardam no banco o texto cru que
// whatsapp.js jogava (err.code === "sem_template"), não o texto fixo de cima -- tryNotifySlot
// só roda nessa vaga de novo se alguém reatribuir, então o banco não se corrige sozinho.
// Reconhece os dois formatos aqui no front em vez de precisar rodar um backfill no banco.
function notifyErrorEhSistemico(notifyError) {
  if (!notifyError) return false;
  if (notifyError === NOTIFY_ERROR_TEMPLATE_PENDENTE) return true;
  return /sem template aprovado/i.test(notifyError);
}

// Icone de alerta ao lado de quem foi escalado, quando o aviso por WhatsApp
// falhou (slot.notifyError, ver routes/escalas.js) -- antes so' tinha o
// atributo nativo `title`, que no touch (celular) nunca aparece e no desktop
// e' discreto demais. Aqui vira um popover proprio, que abre no hover
// (desktop) e no toque (mobile), com o motivo exato que veio da API (ex.:
// "Musico sem WhatsApp cadastrado." ou o erro do envio) -- pedido do Drigo,
// 27/10/2026, vendo os triangulos na tela de Escala de musicos sem entender
// o motivo. `stopPropagation` evita que o toque no icone tambem dispare o
// onClick da vaga (que abriria o seletor de musico por baixo).
function NotifyAlertIcon({ message }) {
  const [open, setOpen] = useState(false);
  if (!message) return null;
  return (
    <span
      style={{ position: "relative", display: "inline-flex", alignItems: "center", flexShrink: 0 }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        role="button"
        tabIndex={0}
        aria-label={message}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }
        }}
        style={{ display: "inline-flex", cursor: "pointer" }}
      >
        <AlertTriangle size={15} color="#FBBF24" />
      </span>
      {open && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            right: 0,
            width: 200,
            background: "#1c2639",
            border: "1px solid rgba(251,191,36,0.4)",
            color: "#F3E9C9",
            fontSize: 11.5,
            fontWeight: 700,
            lineHeight: 1.4,
            padding: "8px 10px",
            borderRadius: 10,
            boxShadow: "0 10px 24px rgba(0,0,0,0.45)",
            zIndex: 20
          }}
        >
          {message}
        </span>
      )}
    </span>
  );
}

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const iconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: 9,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(255,255,255,0.03)",
  color: "#c7ced9",
  cursor: "pointer"
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,8,18,0.72)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 50
};

const modalCardStyle = {
  width: "100%",
  maxWidth: 420,
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.32)",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
  position: "relative",
  maxHeight: "86vh",
  overflowY: "auto"
};

const modalCloseButtonStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "rgba(255,255,255,0.06)",
  borderRadius: 8,
  color: "#9fb0cc",
  cursor: "pointer"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(255,255,255,0.04)",
  color: "#e5e7eb",
  fontSize: 14,
  fontFamily: "inherit"
};

const fieldLabelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 800,
  color: "#9fb0cc"
};

const primaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(125,211,252,0.4)",
  background: "rgba(14,165,233,0.18)",
  color: "#BAE6FD",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

const secondaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "transparent",
  color: "#c7ced9",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer"
};

const dangerButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(248,113,113,0.4)",
  background: "rgba(248,113,113,0.14)",
  color: "#FCA5A5",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

// Vaga como "chip" clicavel (toque simples) -- vazia (tracejada, "Vago") ou
// preenchida (nome do musico). O proprio chip e' o botao que abre a lista.
function slotChipStyle(filled) {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    borderRadius: 10,
    border: filled ? "1px solid rgba(125,211,252,0.35)" : "1px dashed rgba(148,163,184,0.4)",
    background: filled ? "rgba(14,165,233,0.1)" : "rgba(255,255,255,0.02)",
    color: filled ? "#E5F6FF" : "#8d98ab",
    cursor: "pointer",
    fontSize: 13.5,
    fontWeight: 700
  };
}

// Link de ouvir/cifra dentro do chip de musica do repertorio (mesmo padrao
// visual do linkButtonStyle em RepertorioMusico.jsx/AdminRepertorio.jsx, so
// que local -- essa tela nao importa daquelas outras).
const songLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 10.5,
  fontWeight: 800,
  textDecoration: "none",
  padding: "3px 7px",
  borderRadius: 6,
  border: "1px solid rgba(148,163,184,0.28)"
};

const sectionLabelStyle = {
  margin: "0 0 2px",
  fontSize: 11.5,
  color: "#8d98ab",
  textTransform: "uppercase",
  letterSpacing: 0.4
};

const WEEKDAYS_PT = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];
const MONTHS_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

// Monta o texto da escala do mes formatado pra colar direto no WhatsApp
// (*negrito*, _italico_ -- sintaxe nativa do proprio WhatsApp).
function buildMonthScheduleText(escalasDoMes, tituloEscala = "Escala de músicos") {
  if (escalasDoMes.length === 0) return "";
  const [anoRef, mesRef] = String(escalasDoMes[0].date).slice(0, 7).split("-");
  const [anoFim, mesFim] = String(escalasDoMes[escalasDoMes.length - 1].date).slice(0, 7).split("-");
  // Desde que isso passou a trazer "as proximas N escalas" em vez de "as
  // escalas do mes corrente" (29/10/2026), a lista pode atravessar a
  // virada do mes (ex.: 29/11 e 06/12 na mesma lista) -- nesse caso o
  // cabecalho mostra o intervalo de meses em vez de um unico mes que
  // deixaria a ultima data sem contexto.
  const titulo =
    anoRef === anoFim && mesRef === mesFim
      ? `*${tituloEscala} — ${MONTHS_PT[Number(mesRef) - 1]}/${anoRef}*`
      : `*${tituloEscala} — ${MONTHS_PT[Number(mesRef) - 1]}/${anoRef} a ${MONTHS_PT[Number(mesFim) - 1]}/${anoFim}*`;
  const blocos = escalasDoMes.map((escala) => {
    const iso = String(escala.date).slice(0, 10);
    const [y, m, d] = iso.split("-");
    const weekday = WEEKDAYS_PT[new Date(Date.UTC(Number(y), Number(m) - 1, Number(d))).getUTCDay()];
    const linhas = [`*${d}/${m} (${weekday})*${escala.label ? ` · ${escala.label}` : ""}`];
    (escala.slots || []).forEach((slot) => {
      const nome = slot.musicianPerson?.fullName || slot.musician?.name || null;
      linhas.push(`${slot.funcao}: ${nome || "_vago_"}`);
    });
    const indisponiveisNomes = (escala.indisponiveis || [])
      .map((ind) => ind.person?.fullName)
      .filter(Boolean);
    if (indisponiveisNomes.length > 0) {
      linhas.push(`Indisponíveis: ${indisponiveisNomes.join(", ")}`);
    }
    return linhas.join("\n");
  });
  return [titulo, ...blocos].join("\n\n");
}

// Estatisticas de uma escala (quantas vagas tem, quantas estao preenchidas,
// quantas abertas, quantos indisponiveis) -- usado tanto na linha de status
// "X/Y escalados" quanto (29/10/2026, revisao /ux-ui) pro destaque visual
// (borda ambar) do card quando ha vaga aberta, pra nao ter duas contagens
// divergentes se alguma mudar no futuro.
function getEscalaStats(escala) {
  const total = escala.slots.length;
  const filled = escala.slots.filter((s) => s.musicianPerson?.fullName || s.musician?.name).length;
  const open = total - filled;
  const indisponivelCount = (escala.indisponiveis || []).length;
  return { total, filled, open, indisponivelCount };
}

// ---- Imagem visual da escala do mes ("mural"), pra compartilhar no --------
// WhatsApp como figura (pedido do Drigo, 18/10/2026) -- desenhada em
// <canvas>, no mesmo estilo visual do resto do app (fundo azul-marinho
// degrade, cards com borda translucida). Segue o mesmo padrao ja usado em
// AdminAniversariantes.jsx pra gerar a arte de aniversariante (loadImage
// com cache em promise, wrapLines por medida de texto), so' que adaptado
// pra um canvas retangular de altura VARIAVEL (aqui e' uma lista com N
// escalas, nao um cartao quadrado unico com texto curto).
const MURAL_WIDTH = 1080;
const MURAL_FONT = "'DM Sans', system-ui, sans-serif";
const MURAL_HEADER_HEIGHT = 56 + 80 + 24 + 54 + 14 + 38 + 40; // ver drawScheduleMural

function muralLoadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    // Sem logo a imagem ainda sai, so' com texto -- rede lenta ou arquivo
    // indisponivel nao pode quebrar a geracao.
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

let muralLogoPromise = null;
function loadMuralLogo() {
  if (!muralLogoPromise) {
    muralLogoPromise = muralLoadImage("/institucional/logo-mosaico-wordmark.png");
  }
  return muralLogoPromise;
}

function muralWrapLines(ctx, text, maxWidth) {
  const words = String(text || "").split(" ");
  let line = "";
  const lines = [];
  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function muralDrawLines(ctx, lines, x, topY, lineHeight) {
  lines.forEach((l, i) => ctx.fillText(l, x, topY + lineHeight * 0.78 + i * lineHeight));
  return topY + lines.length * lineHeight;
}

function slugifyMuralTitulo(texto) {
  return String(texto || "escala")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// 1a passada: mede cada bloco de texto (sem desenhar) com um contexto
// temporario, pra so' entao saber a altura TOTAL do mural antes de fixar o
// tamanho real do canvas (canvas.height so' pode ser definido uma vez,
// senao limpa o que ja foi desenhado -- por isso medir primeiro, desenhar
// depois).
function layoutScheduleMural(ctx, escalasDoMes) {
  const padX = 64;
  const contentW = MURAL_WIDTH - padX * 2;
  const cardPad = 32;
  const cardContentW = contentW - cardPad * 2;
  let y = MURAL_HEADER_HEIGHT;

  const cards = escalasDoMes.map((escala) => {
    const cardTop = y;
    let cy = cardPad;

    const iso = String(escala.date).slice(0, 10);
    const [yy, mm, dd] = iso.split("-");
    const weekday = WEEKDAYS_PT[new Date(Date.UTC(Number(yy), Number(mm) - 1, Number(dd))).getUTCDay()];
    const dateLabel = `${dd}/${mm} · ${weekday}${escala.label ? ` · ${escala.label}` : ""}`;
    ctx.font = `900 32px ${MURAL_FONT}`;
    const dateLines = muralWrapLines(ctx, dateLabel, cardContentW);
    cy += dateLines.length * 40;

    const slots = escala.slots || [];
    const total = slots.length;
    const filled = slots.filter((s) => s.musicianPerson?.fullName || s.musician?.name).length;
    const open = total - filled;
    const indisponiveisNomes = (escala.indisponiveis || []).map((ind) => ind.person?.fullName).filter(Boolean);
    const statusText = total === 0
      ? "Sem vagas cadastradas"
      : `${filled}/${total} escalados${open > 0 ? ` · ${open} vaga${open > 1 ? "s" : ""} aberta${open > 1 ? "s" : ""}` : ""}${indisponiveisNomes.length > 0 ? ` · ${indisponiveisNomes.length} indisponível${indisponiveisNomes.length > 1 ? "eis" : ""}` : ""}`;
    cy += 14 + 34;

    ctx.font = `600 28px ${MURAL_FONT}`;
    const slotLines = slots.map((slot) => {
      const nome = slot.musicianPerson?.fullName || slot.musician?.name || null;
      const lines = muralWrapLines(ctx, `${slot.funcao}: ${nome || "Vaga aberta"}`, cardContentW);
      cy += lines.length * 36 + 6;
      return { lines, open: !nome };
    });

    let indispLines = [];
    if (indisponiveisNomes.length > 0) {
      cy += 10;
      ctx.font = `600 24px ${MURAL_FONT}`;
      indispLines = muralWrapLines(ctx, `Indisponíveis: ${indisponiveisNomes.join(", ")}`, cardContentW);
      cy += indispLines.length * 32;
    }

    const songsList = escala.songs || [];
    let songHeaderLines = [];
    let songLines = [];
    if (songsList.length > 0) {
      cy += 14;
      ctx.font = `800 24px ${MURAL_FONT}`;
      songHeaderLines = muralWrapLines(ctx, "Repertório", cardContentW);
      cy += songHeaderLines.length * 30 + 4;

      ctx.font = `600 24px ${MURAL_FONT}`;
      songLines = songsList.map((es) => {
        const titulo = es.song?.title || "Música";
        const tom = es.key || es.song?.key;
        const lines = muralWrapLines(ctx, tom ? `• ${titulo} (${tom})` : `• ${titulo}`, cardContentW);
        cy += lines.length * 30;
        return lines;
      });
    }

    cy += cardPad;
    y += cy + 24; // gap entre cards

    return { cardTop, cardHeight: cy, dateLines, statusText, slotLines, indispLines, songHeaderLines, songLines };
  });

  y += 56; // rodape

  return { totalHeight: y, cards, padX, cardPad, contentW, cardContentW };
}

// 2a passada: com a altura ja' calculada, fixa o tamanho real do canvas e
// desenha tudo (fundo, logo, titulo, cada card). `assets.logo` pode vir
// null (rede lenta/arquivo indisponivel) -- a arte sai so' sem o logo.
function drawScheduleMural(canvas, escalasDoMes, tituloMes, assets, tituloEscala = "Escala de Músicos") {
  const measureCtx = document.createElement("canvas").getContext("2d");
  const layout = layoutScheduleMural(measureCtx, escalasDoMes);

  canvas.width = MURAL_WIDTH;
  canvas.height = Math.ceil(layout.totalHeight);
  const ctx = canvas.getContext("2d");

  // Fundo: mesmo degrade azul-marinho dos cards do app (ver cardStyle),
  // so' que vertical (o canvas aqui e' bem mais alto que largo).
  const bg = ctx.createLinearGradient(0, 0, 0, canvas.height);
  bg.addColorStop(0, "#0b1120");
  bg.addColorStop(1, "#131f34");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Textura leve (circulos translucidos), mesmo truque de
  // AdminAniversariantes.jsx (drawSoftGlowCircles) pra nao ficar um fundo
  // chapado, sem precisar de nenhuma imagem externa.
  [[0.08, 0.05, 0.1], [0.94, 0.03, 0.08], [0.06, 0.98, 0.07], [0.97, 0.55, 0.09]].forEach(([fx, fy, fr]) => {
    ctx.beginPath();
    ctx.fillStyle = "rgba(94,146,255,0.06)";
    ctx.arc(fx * canvas.width, fy * canvas.height, fr * canvas.width, 0, Math.PI * 2);
    ctx.fill();
  });

  const centerX = canvas.width / 2;
  ctx.textAlign = "center";

  if (assets?.logo) {
    const logoH = 80;
    const logoW = (assets.logo.width / assets.logo.height) * logoH;
    ctx.drawImage(assets.logo, centerX - logoW / 2, 56, logoW, logoH);
  }

  ctx.fillStyle = "#ffffff";
  ctx.font = `900 46px ${MURAL_FONT}`;
  ctx.fillText(tituloEscala, centerX, 56 + 80 + 24 + 40);

  ctx.fillStyle = "#F3A952";
  ctx.font = `800 30px ${MURAL_FONT}`;
  ctx.fillText(tituloMes, centerX, 56 + 80 + 24 + 54 + 14 + 28);

  ctx.textAlign = "left";
  layout.cards.forEach((card) => {
    const x = layout.padX;
    const w = layout.contentW;
    const top = card.cardTop;
    const h = card.cardHeight;
    const r = 18;

    ctx.beginPath();
    ctx.moveTo(x + r, top);
    ctx.arcTo(x + w, top, x + w, top + h, r);
    ctx.arcTo(x + w, top + h, x, top + h, r);
    ctx.arcTo(x, top + h, x, top, r);
    ctx.arcTo(x, top, x + w, top, r);
    ctx.closePath();
    ctx.fillStyle = "rgba(255,255,255,0.045)";
    ctx.fill();
    ctx.strokeStyle = "rgba(120,152,210,0.3)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    let ty = top + layout.cardPad;
    const tx = x + layout.cardPad;

    ctx.fillStyle = "#ffffff";
    ctx.font = `900 32px ${MURAL_FONT}`;
    ty = muralDrawLines(ctx, card.dateLines, tx, ty, 40);

    ctx.fillStyle = card.statusText.includes("vaga") ? "#FCD34D" : "#4ADE80";
    ctx.font = `800 24px ${MURAL_FONT}`;
    ctx.fillText(card.statusText, tx, ty + 24);
    ty += 14 + 34;

    card.slotLines.forEach((slot) => {
      ctx.fillStyle = slot.open ? "rgba(252,211,77,0.9)" : "#e5e7eb";
      ctx.font = `600 28px ${MURAL_FONT}`;
      ty = muralDrawLines(ctx, slot.lines, tx, ty, 36) + 6;
    });

    if (card.indispLines.length > 0) {
      ty += 10;
      ctx.fillStyle = "rgba(252,165,165,0.85)";
      ctx.font = `600 24px ${MURAL_FONT}`;
      ty = muralDrawLines(ctx, card.indispLines, tx, ty, 32);
    }

    if (card.songLines.length > 0) {
      ty += 14;
      ctx.fillStyle = "#F3A952";
      ctx.font = `800 24px ${MURAL_FONT}`;
      ty = muralDrawLines(ctx, card.songHeaderLines, tx, ty, 30) + 4;

      ctx.fillStyle = "#cfd8ec";
      ctx.font = `600 24px ${MURAL_FONT}`;
      card.songLines.forEach((lines) => {
        ty = muralDrawLines(ctx, lines, tx, ty, 30);
      });
    }
  });

  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(199,206,217,0.7)";
  ctx.font = `600 22px ${MURAL_FONT}`;
  ctx.fillText("Igreja Batista Mosaico · batistamosaico.com.br", centerX, canvas.height - 28);
}

function formatDate(value) {
  if (!value) return "";
  const iso = String(value).slice(0, 10);
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

// Quantos dias antes (ou depois) do culto a pessoa avisou que ficaria
// indisponivel -- comparando so' as datas (sem hora), pra "avisou no dia
// anterior" dar 1 mesmo que tenha sido as 23h. Pedido do Drigo, 27/10/2026:
// saber a antecedencia do aviso, nao so' que a pessoa avisou.
function diasDeAntecedencia(dataEscala, dataAviso) {
  if (!dataEscala || !dataAviso) return null;
  const culto = new Date(`${String(dataEscala).slice(0, 10)}T00:00:00Z`);
  const aviso = new Date(`${String(dataAviso).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(culto.getTime()) || Number.isNaN(aviso.getTime())) return null;
  return Math.round((culto - aviso) / 86400000);
}

function textoAvisoIndisponivel(dataEscala, dataAviso) {
  if (!dataAviso) return null;
  const quando = formatDate(dataAviso);
  const dias = diasDeAntecedencia(dataEscala, dataAviso);
  if (dias === null) return `Avisou em ${quando}`;
  if (dias > 0) return `Avisou em ${quando} (${dias} dia${dias > 1 ? "s" : ""} antes do culto)`;
  if (dias === 0) return `Avisou em ${quando} (no dia do culto)`;
  const depois = Math.abs(dias);
  return `Avisou em ${quando} (${depois} dia${depois > 1 ? "s" : ""} depois do culto)`;
}

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

const EMPTY_ESCALA_FORM = { date: "", label: "", notes: "" };

// Tipos de escala (ministerios) -- pedido do Rodrigo/Carla, 28/10/2026:
// alem da escala de Musicos ja existente, agora tambem Zeladoria/Kids/Teens,
// tudo na mesma tela (abas), mesmo motor (so' muda o `tipo` mandado pra API
// e a flag da Pessoa que decide quem aparece no picker -- ver TIPO_FLAG
// espelhado em routes/escalas.js, mosaico-api).
// `flag` = campo correspondente em Person (ESCALA_TIPO_FLAG espelhado em
// lib/person.js, mosaico-api) -- usado so' aqui no front pra decidir, pra
// quem nao e' ADMIN, quais abas a propria Person da acesso de
// autovisualizacao (ver efeito de /auth/me mais abaixo). O backend quem
// de fato garante isso' e' so' decoracao de UI.
const TIPO_OPTIONS = [
  { value: "MUSICOS", label: "Louvor", flag: "isMusician" },
  { value: "ZELADORIA", label: "Zeladoria", flag: "isZelador" },
  { value: "KIDS", label: "Kids", flag: "isKidsVoluntario" },
  { value: "TEENS", label: "Teens", flag: "isTeensVoluntario" },
  { value: "MIDIA", label: "Mídia", flag: "isMidiaVoluntario" },
  { value: "CAFE", label: "Café", flag: "isCafeVoluntario" },
  { value: "RECEPCAO", label: "Recepção", flag: "isRecepcaoVoluntario" },
  { value: "PODCAST", label: "Podcast", flag: "isPodcastVoluntario" },
  { value: "PREGACAO", label: "Pregação", flag: "isPregacaoVoluntario" },
  { value: "TEENS_DEVOCIONAL", label: "Devocional Teens", flag: "isTeensDevocionalVoluntario" }
];
const TIPO_LABEL = Object.fromEntries(TIPO_OPTIONS.map((t) => [t.value, t.label]));
// Nome do checkbox correspondente no cadastro da pessoa em Membros -- so'
// pra mensagem de "ninguem marcado ainda" apontar pro lugar certo.
const TIPO_CHECKBOX_LABEL = {
  MUSICOS: '"Músico"',
  ZELADORIA: '"Zeladoria"',
  KIDS: '"Kids"',
  TEENS: '"Teens"',
  MIDIA: '"Mídia"',
  CAFE: '"Café"',
  RECEPCAO: '"Recepção"',
  PODCAST: '"Podcast"',
  PREGACAO: '"Pregação"',
  TEENS_DEVOCIONAL: '"Devocional Teens"'
};

export default function AdminEscalas() {
  const [status, setStatus] = useState("checking");
  const [escalas, setEscalas] = useState([]);
  const [musicos, setMusicos] = useState([]);
  // Coordenadores da aba aberta (ver model EscalaCoordenador) -- gerencio
  // so' aparece pra ADMIN de verdade (isAdminUser): decidir QUEM coordena
  // continua privilegio de admin, mesmo que o proprio coordenador ja
  // edite a escala do tipo dele (ver canEdit acima). Pedido do Drigo,
  // 29/10/2026.
  const [coordenadores, setCoordenadores] = useState([]);
  const [coordSearch, setCoordSearch] = useState("");
  const [coordSearchResults, setCoordSearchResults] = useState([]);
  const [coordSearching, setCoordSearching] = useState(false);
  const [coordBusyId, setCoordBusyId] = useState(null);
  // Qual ministerio esta selecionado agora (aba) -- MUSICOS por padrao,
  // mesmo comportamento de sempre pra quem abre a tela pela 1a vez, EXCETO
  // quando a URL vem com ?tipo=ZELADORIA (ou outro tipo valido) -- usado
  // pelo lembrete de escala acabando (e-mail/push), que assim pode linkar
  // direto pra aba certa em vez de sempre cair em Louvor (pedido do Drigo,
  // 30/10/2026).
  const [tipo, setTipo] = useState(() => {
    const paramTipo = new URLSearchParams(window.location.search).get("tipo")?.toUpperCase();
    return paramTipo && TIPO_LABEL[paramTipo] ? paramTipo : "MUSICOS";
  });
  // Repertorio (Song) pro picker de "+ Musica" -- mesma lista que GET
  // /songs ja devolve pra tela de Repertorio (so' o catalogo ativo).
  const [repertorio, setRepertorio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  // Mensagem de sucesso/info (verde) -- separada de `feedback` (erro, vermelho)
  // pra "12 escalas criadas" nao parecer um erro.
  const [notice, setNotice] = useState("");
  // Mensagem neutra (nem erro nem sucesso) -- ate 29/10/2026 "nao tem nada
  // pra copiar ainda" usava o canal `feedback` (vermelho de erro), o que
  // alarmava sem motivo (achado do Drigo na revisao /ux-ui de /admin/escalas:
  // a caixa vermelha parecia uma falha real). Mensagens tipo "ainda nao tem
  // nada aqui" entram aqui em vez disso.
  const [info, setInfo] = useState("");
  // ADMIN sempre edita; alem disso, desde 29/10/2026, quem for coordenador
  // (ver model EscalaCoordenador, mosaico-api) do tipo da aba aberta tambem
  // edita -- so' aquele tipo, nao os outros (pedido do Drigo: "perfil admin
  // pra ser selecionado o nome em cada tipo de escala, que possam editar
  // aquela escala"). Guarda os dois ingredientes "crus" (admin de verdade e
  // a lista de tipos coordenados) em vez de um booleano fixo, porque
  // `canEdit` agora depende da aba (`tipo`) selecionada -- ver const logo
  // abaixo. NAO e' afetado pelo "Ver como" de proposito (mesmo racional do
  // texto em AdminHome.jsx: "nao muda nenhuma permissao de verdade, so' a
  // visao").
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [coordenadorTipos, setCoordenadorTipos] = useState([]);
  // Derivado (nao e' state): se a aba ABERTA agora (`tipo`) e' editavel por
  // quem esta logado. Recalcula sozinho quando a pessoa troca de aba.
  const canEdit = isAdminUser || coordenadorTipos.includes(tipo);
  // Pra quem nao e' ADMIN: lista de tipos (ESCALA_TIPO_FLAG) que a propria
  // Person tem marcado -- decide qual aba abre por padrao e quais aparecem
  // no seletor (so' as que a pessoa pode de fato ver, nunca todas as 10).
  // ADMIN continua vendo o seletor completo de sempre (ver TIPO_OPTIONS
  // direto no render), este estado so' importa pra quem nao e' ADMIN.
  const [accessibleTipos, setAccessibleTipos] = useState([]);
  // Person da propria conta (so' preenche se a conta tiver uma Person
  // vinculada) -- usada pro auto-servico de "marcar que nao vou poder
  // tocar" quando quem ve' a tela nao e' ADMIN (ver handleMarkSelfIndisponivel).
  const [ownPersonId, setOwnPersonId] = useState(null);
  // "Minhas escalas" -- filtro pra quem e' musico so' ver as datas onde ELE
  // esta escalado, em vez de rolar a lista toda (pedido do Drigo,
  // 22/10/2026, junto da pergunta que o Barnabe passou a poder responder).
  // So' aparece o botao quando ownPersonId existe (ownPersonId vem de
  // auth/me -- se a conta nao tem Person vinculada, nao ha "minhas" pra
  // filtrar).
  const [onlyMine, setOnlyMine] = useState(false);

  // Modal "Nova escala".
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ESCALA_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  // "Usar como modelo" (pedido do Drigo, 29/10/2026: recriar a mesma
  // estrutura de vagas de uma escala anterior em vez de montar do zero
  // toda vez). As opcoes vem de um fetch separado (com all=1, pra incluir
  // escalas passadas -- a lista principal `escalas` so' traz hoje em
  // diante) carregado so' quando o modal abre. Por padrao as vagas nascem
  // vazias (so' a estrutura); o checkbox e' pra quem quer repetir tambem
  // quem tocou (ex.: Zeladoria/Kids, onde o mesmo grupo costuma repetir).
  const [modeloOptions, setModeloOptions] = useState([]);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [modeloEscalaId, setModeloEscalaId] = useState("");
  const [copyAssigned, setCopyAssigned] = useState(false);

  // Inline "+ vaga" por escala -- qual escala tem o campo aberto e o texto
  // digitado nele (um por vez, guardado por id da escala).
  const [slotFormOpenFor, setSlotFormOpenFor] = useState(null);
  const [slotFuncao, setSlotFuncao] = useState("");
  const [savingSlot, setSavingSlot] = useState(false);

  // Picker "escolher musico" (toque simples) -- qual vaga esta sendo
  // escalada agora.
  const [assignTarget, setAssignTarget] = useState(null); // { slotId, escalaId, funcao }
  const [assigning, setAssigning] = useState(false);

  // Picker "+ musica" (linka uma musica do repertorio na escala) -- qual
  // escala esta recebendo a musica agora.
  const [addSongTarget, setAddSongTarget] = useState(null); // escalaId
  const [addingSong, setAddingSong] = useState(false);
  const [deleteSongTarget, setDeleteSongTarget] = useState(null); // { id, escalaId, title }

  // Picker "+ indisponivel" (marca alguem como indisponivel NESSA escala --
  // bloqueia ela de ser escalada ali, ver escalas.js EscalaIndisponivel).
  const [addIndisponivelTarget, setAddIndisponivelTarget] = useState(null); // escalaId
  const [addingIndisponivel, setAddingIndisponivel] = useState(false);
  const [deleteIndisponivelTarget, setDeleteIndisponivelTarget] = useState(null); // { id, escalaId, fullName }

  // Busca compartilhada pelos 3 pickers de toque simples (musico, musica,
  // indisponivel) -- so um fica aberto por vez, entao um estado so' serve
  // -- e zerada toda vez que um picker eh aberto (ver os onClick que
  // abrem cada um). Pedido da revisao de UX, 17/10/2026: lista sem busca
  // nao escala conforme repertorio/pessoas crescem.
  const [pickerSearch, setPickerSearch] = useState("");

  // Confirmacao de exclusao (escala inteira, ou so uma vaga) -- sem
  // window.confirm, mesmo padrao do Repertorio.
  const [deleteEscalaTarget, setDeleteEscalaTarget] = useState(null);
  const [deleteSlotTarget, setDeleteSlotTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Editar a data de uma escala ja criada -- antes so dava pra criar (data
  // fixa) ou excluir; pedido do Drigo, 28/10/2026, pro gerador semanal de
  // Zeladoria (sabado): numa semana especifica o mutirao cai numa sexta em
  // vez de sabado, e precisa poder ajustar so' aquela data em vez de
  // excluir e recriar a escala inteira (perderia vagas/pessoas ja
  // preenchidas).
  const [editDateTarget, setEditDateTarget] = useState(null); // id da escala sendo editada
  const [editDateValue, setEditDateValue] = useState("");
  const [editLabelValue, setEditLabelValue] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  // "Gerar domingos do mes" -- pedido do Drigo (17/10/2026, ajustado no
  // mesmo dia de "ate o fim do ano" pra "do mes"): criar de uma vez uma
  // escala vazia (sem vaga, so a data) pra cada domingo daqui ate o fim do
  // mes corrente, pra ele so preencher/cancelar (excluir) os que nao forem
  // acontecer, em vez de criar uma por uma manualmente.
  const [generatingSundays, setGeneratingSundays] = useState(false);
  // So' fica habilitado quando sobra pelo menos um domingo deste mes sem
  // escala -- pedido do Drigo, 20/10/2026: "precisa ser ativado so' se ja
  // n foi gerado, pra nao matar a escala ja montada" (o backend ja pulava
  // datas existentes, mas o botao continuava clicavel sempre, o que dava
  // margem a duvida/susto).
  const weeklyGeneratorConfig = getWeeklyGeneratorConfig(tipo);
  const pendingSundaysThisMonth = useMemo(
    () => getPendingWeekdaysThisMonth(escalas, weeklyGeneratorConfig.weekday),
    [escalas, weeklyGeneratorConfig.weekday]
  );

  // Lista mesmo (o que renderiza) -- so' entra em cena quando onlyMine esta
  // ligado e a conta tem Person vinculada; senao e' a lista toda, igual
  // sempre foi.
  const visibleEscalas = useMemo(() => {
    if (!onlyMine || !ownPersonId) return escalas;
    return escalas.filter((escala) => (escala.slots || []).some((s) => s.musicianPerson?.id === ownPersonId));
  }, [escalas, onlyMine, ownPersonId]);

  // Resumo de pendencias no topo da aba (revisao /ux-ui, 29/10/2026,
  // prototipo aprovado pelo Drigo com "pode seguir") -- antes era preciso
  // ler card por card pra saber se tinha vaga aberta em algum domingo; aqui
  // soma de uma vez e aponta pro primeiro card pendente via scroll.
  const pendingSummary = useMemo(() => {
    let totalOpenVagas = 0;
    let escalasComVaga = 0;
    let firstOpenEscalaId = null;
    visibleEscalas.forEach((escala) => {
      const stats = getEscalaStats(escala);
      if (stats.open > 0) {
        totalOpenVagas += stats.open;
        escalasComVaga += 1;
        if (firstOpenEscalaId === null) firstOpenEscalaId = escala.id;
      }
    });
    return { totalOpenVagas, escalasComVaga, firstOpenEscalaId };
  }, [visibleEscalas]);

  function handleScrollToFirstOpenVaga() {
    if (!pendingSummary.firstOpenEscalaId) return;
    const node = document.getElementById(`escala-${pendingSummary.firstOpenEscalaId}`);
    if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // "Copiar escala do mes" -- monta o texto formatado (ver
  // buildMonthScheduleText acima) e copia pra area de transferencia, pra
  // colar direto num grupo do WhatsApp (pedido do Drigo, 18/10/2026).
  const [copyingSchedule, setCopyingSchedule] = useState(false);

  // "Gerar imagem do mes" -- versao visual (imagem), alem do texto copiavel
  // acima, pra quem prefere mandar como figura no grupo/status do WhatsApp
  // em vez de colar texto (pedido do Drigo, 18/10/2026). Mesmo recorte de
  // "mes atual" do copiar-texto; desenhada em <canvas> (ver
  // drawScheduleMural acima).
  const [muralOpen, setMuralOpen] = useState(false);
  const [muralEscalas, setMuralEscalas] = useState([]);
  const [muralTitulo, setMuralTitulo] = useState("");
  const [muralReady, setMuralReady] = useState(false);
  const muralCanvasRef = useRef(null);


  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const roles = getEffectiveRoles(me);
        // Mesmo racional do canSeeEscala em InstitutionalLayout.jsx: a
        // capacidade de musico mora na Person (isMusician), nao em
        // User.roles -- corrigido 20/10/2026 (Gabriel ficou sem acesso
        // mesmo escalado, por causa de um merge de cadastro que nunca
        // sincronizou User.roles). roles.includes("MUSICO") continua
        // sendo o atalho mais comum; quem nao tem o papel na conta ainda
        // entra se a Person vinculada estiver isMusician=true.
        const realIsAdminUser = Boolean(me?.roles?.includes("ADMIN"));
        const isMusicianByPerson = getSimulatedIsMusician(realIsAdminUser, me?.person?.isMusician);
        const isAdmin = roles.includes("ADMIN");
        // Generalizacao de 28/10/2026: alem de MUSICOS (unico que tinha
        // autoacesso ate entao), qualquer tipo cuja flag a propria Person
        // tenha marcada (ver TIPO_OPTIONS.flag acima e ESCALA_TIPO_FLAG em
        // lib/person.js, mosaico-api -- mesmo mapa, duas pontas). Desde
        // 29/10/2026 o "Ver como" tambem simula as outras 9 abas
        // (getSimulatedMinistryFlag, ver roleSimulator.js) -- antes so'
        // cobria Musico.
        const ownTipos = TIPO_OPTIONS
          .filter((opt) => (opt.value === "MUSICOS" ? isMusicianByPerson || roles.includes("MUSICO") : getSimulatedMinistryFlag(realIsAdminUser, opt.flag, me?.person?.[opt.flag])))
          .map((opt) => opt.value);
        // Tipos que a Person coordena (ver model EscalaCoordenador) --
        // independente de servir no ministerio (pedido do Drigo,
        // 29/10/2026: coordenador e' um papel a parte, nao exige a flag
        // isZelador/isKidsVoluntario/etc), entao entra numa lista separada
        // e e' SOMADO ao ownTipos pra decidir aba padrao/abas visiveis,
        // sem depender do "Ver como" (sempre a Person REAL, nunca
        // simulada -- ver comentario no state acima).
        const coordTipos = (me?.person?.escalaCoordenacoes || []).map((c) => c.tipo);
        const tiposComAcesso = Array.from(new Set([...ownTipos, ...coordTipos]));
        if ((isAdmin || tiposComAcesso.length > 0) && !me.passwordMustChange) {
          setStatus("ready");
          setIsAdminUser(isAdmin);
          setCoordenadorTipos(coordTipos);
          setOwnPersonId(me?.person?.id || null);
          setAccessibleTipos(tiposComAcesso);
          // Quem nao e' ADMIN abre direto na propria aba (1a que tiver
          // acesso) em vez de ficar preso em MUSICOS por padrao, caso a
          // pessoa nao seja musico mas sim, por ex., so' de Zeladoria (ou
          // coordenadora de um tipo que nem serve).
          if (!isAdmin && tiposComAcesso.length > 0 && !tiposComAcesso.includes(tipo)) {
            setTipo(tiposComAcesso[0]);
          }
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  const loadEscalas = useCallback(async () => {
    setLoading(true);
    setFeedback("");
    setNotice("");
    setInfo("");
    try {
      const data = await apiFetch(`/escalas?tipo=${encodeURIComponent(tipo)}`, { method: "GET" });
      setEscalas(Array.isArray(data) ? data : []);
    } catch (err) {
      setFeedback(err?.error || "Não foi possível carregar as escalas agora.");
    } finally {
      setLoading(false);
    }
  }, [tipo]);

  // Opcoes pro seletor "Usar como modelo" do modal "Nova escala" -- usa
  // all=1 de proposito (a lista principal `escalas` so' traz hoje em
  // diante, mas o modelo mais comum e' justamente a escala do culto
  // passado). So' carrega quando o modal abre, nao em todo loadEscalas.
  const loadModeloOptions = useCallback(async () => {
    setLoadingModelos(true);
    try {
      const data = await apiFetch(`/escalas?all=1&tipo=${encodeURIComponent(tipo)}`, { method: "GET" });
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => new Date(b.date) - new Date(a.date));
      setModeloOptions(list.slice(0, 8));
    } catch (_) {
      setModeloOptions([]);
    } finally {
      setLoadingModelos(false);
    }
  }, [tipo]);

  const loadMusicos = useCallback(async () => {
    try {
      const data = await apiFetch(`/escalas/musicos?tipo=${encodeURIComponent(tipo)}`, { method: "GET" });
      setMusicos(Array.isArray(data) ? data : []);
    } catch (_) {
      // lista de musicos so alimenta o picker -- falha aqui nao trava a tela.
    }
  }, [tipo]);

  const loadRepertorio = useCallback(async () => {
    try {
      const data = await apiFetch("/songs", { method: "GET" });
      setRepertorio(Array.isArray(data) ? data : []);
    } catch (_) {
      // lista de musicas so alimenta o picker -- falha aqui nao trava a tela.
    }
  }, []);

  const loadCoordenadores = useCallback(async () => {
    try {
      const data = await apiFetch(`/escalas/coordenadores?tipo=${encodeURIComponent(tipo)}`, { method: "GET" });
      setCoordenadores(Array.isArray(data) ? data : []);
    } catch (_) {
      // lista de coordenadores so' alimenta o bloco de gerenciamento --
      // falha aqui nao trava a tela.
    }
  }, [tipo]);

  useEffect(() => {
    if (status === "ready") {
      loadEscalas();
      loadMusicos();
      loadRepertorio();
    }
  }, [status, loadEscalas, loadMusicos, loadRepertorio]);

  // So' ADMIN gerencia coordenadores (ver bloco na UI) -- so' carrega a
  // lista pra quem de fato ve' o bloco, pra nao gastar chamada a toa.
  useEffect(() => {
    if (status === "ready" && isAdminUser) {
      loadCoordenadores();
    }
  }, [status, isAdminUser, loadCoordenadores]);

  // Busca com debounce pra adicionar coordenador -- qualquer Person pelo
  // nome (nao so' quem ja serve no ministerio, ver GET /escalas/pessoas em
  // escalas.js), minimo 2 letras pra nao buscar a cada tecla à toa.
  useEffect(() => {
    if (!isAdminUser) return;
    const termo = coordSearch.trim();
    if (termo.length < 2) {
      setCoordSearchResults([]);
      setCoordSearching(false);
      return;
    }
    setCoordSearching(true);
    const handle = setTimeout(() => {
      apiFetch(`/escalas/pessoas?search=${encodeURIComponent(termo)}`, { method: "GET" })
        .then((data) => setCoordSearchResults(Array.isArray(data) ? data : []))
        .catch(() => setCoordSearchResults([]))
        .finally(() => setCoordSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [coordSearch, isAdminUser]);

  async function handleAddCoordenador(personId) {
    setCoordBusyId(personId);
    try {
      await apiPost("/escalas/coordenadores", { body: { personId, tipo } });
      setCoordSearch("");
      setCoordSearchResults([]);
      await loadCoordenadores();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível adicionar esse coordenador.");
    } finally {
      setCoordBusyId(null);
    }
  }

  async function handleRemoveCoordenador(coordenador) {
    setCoordBusyId(coordenador.id);
    try {
      await apiDelete(`/escalas/coordenadores/${coordenador.id}`);
      setCoordenadores((prev) => prev.filter((c) => c.id !== coordenador.id));
    } catch (err) {
      setFeedback(err?.error || "Não foi possível remover esse coordenador.");
    } finally {
      setCoordBusyId(null);
    }
  }

  // Desenha a imagem (mural) assim que o modal abre (precisa que o <canvas>
  // ja' esteja montado no DOM, por isso e' um efeito e nao parte direto do
  // handleOpenMural) -- espera as fontes carregarem primeiro (mesmo motivo
  // de AdminAniversariantes.jsx: sem isso o canvas pode desenhar com a
  // fonte padrao do sistema no instante em que o modal abre).
  // FIX 18/10/2026: esse hook estava declarado DEPOIS dos `return` condicionais
  // de status "checking"/"forbidden" abaixo -- violava a regra de hooks (ordem
  // tem que ser identica em todo render) e quebrava a tela inteira em branco
  // assim que o status saia de "checking" pra "ready" (React error #310).
  // Hooks tem que vir todos ANTES de qualquer return condicional.
  useEffect(() => {
    if (!muralOpen || muralEscalas.length === 0) return;
    let alive = true;
    const draw = async () => {
      const canvas = muralCanvasRef.current;
      if (!canvas) return;
      const logo = await loadMuralLogo();
      if (!alive) return;
      // Antes sempre vinha "Escala de Músicos" fixo na imagem, mesmo gerando
      // de outra aba (ex.: Zeladoria) -- passa o titulo certo do tipo
      // selecionado agora que outros ministerios tem gente de verdade
      // usando a tela (ver TIPO_LABEL acima).
      drawScheduleMural(canvas, muralEscalas, muralTitulo, { logo }, `Escala de ${TIPO_LABEL[tipo] || "Músicos"}`);
      if (alive) setMuralReady(true);
    };
    if (document.fonts?.ready) {
      document.fonts.ready.then(draw).catch(draw);
    } else {
      draw();
    }
    return () => {
      alive = false;
    };
  }, [muralOpen, muralEscalas, muralTitulo]);

  if (status === "checking") return <CenteredMessage>Carregando…</CenteredMessage>;
  if (status === "forbidden") return <CenteredMessage>Você não tem acesso a esta área.</CenteredMessage>;

  async function handleCreateEscala(event) {
    event.preventDefault();
    if (!form.date) {
      setFormError("Escolha uma data.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const created = await apiPost("/escalas", { body: { ...form, tipo } });
      // "Usar como modelo": recria a mesma lista de vagas da escala
      // escolhida, por padrao vazias (so' a estrutura -- funcao + ordem).
      // Quando copyAssigned esta marcado, tambem escala quem estava em
      // cada vaga na escala-modelo (ignorando vagas que estavam vazias).
      // Feito em sequencia (nao Promise.all) pra manter a ordem das vagas
      // e simplificar o tratamento de erro parcial.
      if (modeloEscalaId) {
        const modelo = modeloOptions.find((item) => item.id === modeloEscalaId);
        const modeloSlots = modelo?.slots || [];
        for (const slot of modeloSlots) {
          const novaVaga = await apiPost(`/escalas/${created.id}/slots`, {
            body: { funcao: slot.funcao, order: slot.order }
          });
          if (copyAssigned && slot.musicianPerson?.id) {
            await apiPost(`/escalas/slots/${novaVaga.id}/assign`, {
              body: { musicianPersonId: slot.musicianPerson.id }
            });
          }
        }
      }
      setFormOpen(false);
      setForm(EMPTY_ESCALA_FORM);
      setModeloEscalaId("");
      setCopyAssigned(false);
      await loadEscalas();
    } catch (err) {
      setFormError(err?.error || "Não foi possível salvar a escala.");
    } finally {
      setSaving(false);
    }
  }

  function startEditDate(escala) {
    setEditDateTarget(escala.id);
    setEditDateValue(String(escala.date).slice(0, 10));
    setEditLabelValue(escala.label || "");
    setFeedback("");
  }

  function cancelEditDate() {
    setEditDateTarget(null);
    setEditDateValue("");
    setEditLabelValue("");
  }

  async function saveEditDate(escalaId) {
    if (!editDateValue) return;
    setSavingDate(true);
    setFeedback("");
    try {
      await apiPatch(`/escalas/${escalaId}`, { body: { date: editDateValue, label: editLabelValue.trim() || null } });
      setEditDateTarget(null);
      setEditDateValue("");
      setEditLabelValue("");
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível alterar a data/tema dessa escala.");
    } finally {
      setSavingDate(false);
    }
  }

  // Gera uma escala (sem vaga, so a data) pra cada domingo entre hoje e
  // 31/12 do ano corrente, pulando datas que ja tem escala criada. Cria uma
  // de cada vez (nao em paralelo) pra nao sobrecarregar e pra contar
  // direito quantas deram certo.
  async function handleGenerateSundays() {
    setGeneratingSundays(true);
    setFeedback("");
    try {
      // Reconfere contra o estado mais recente (nao so' o memo), pra cobrir
      // o caso raro de ter mudado entre o render e o clique.
      const config = getWeeklyGeneratorConfig(tipo);
      const datas = getPendingWeekdaysThisMonth(escalas, config.weekday);

      if (datas.length === 0) {
        setNotice(`Já existe escala pra todos os próximos ${config.plural}.`);
        return;
      }

      let created = 0;
      let failed = 0;
      for (const date of datas) {
        try {
          const escala = await apiPost("/escalas", { body: { date, label: config.defaultLabel, notes: "", tipo } });
          // Alguns tipos (ex.: Devocional Teens) tem sempre as mesmas vagas
          // fixas -- cria elas ja' junto, pra escala nao nascer vazia
          // ("Nenhuma vaga ainda", mesmo bug que aconteceu com a Zeladoria
          // antes de 28/10/2026 quando o import rodava por fora).
          if (Array.isArray(config.defaultSlots) && config.defaultSlots.length > 0 && escala?.id) {
            for (const funcao of config.defaultSlots) {
              try {
                await apiPost(`/escalas/${escala.id}/slots`, { body: { funcao } });
              } catch (_) {
                /* a escala foi criada mesmo se uma vaga padrao falhar -- da' pra adicionar na mao depois */
              }
            }
          }
          created++;
        } catch (_) {
          failed++;
        }
      }

      await loadEscalas();
      if (failed > 0) {
        setFeedback(`${created} escala(s) criada(s), ${failed} falharam -- tenta de novo pra essas.`);
      } else {
        setNotice(`${created} escala(s) criada(s) pros próximos ${config.plural}. Dá pra editar a data de qualquer uma (ex.: a semana que cai numa sexta em vez de ${config.singular}) e cancelar (excluir) as que não forem acontecer.`);
      }
    } finally {
      setGeneratingSundays(false);
    }
  }

  function getEscalasDoProximoMes(escalasOrdenadas) {
    if (escalasOrdenadas.length === 0) return [];
    // Janela cobre o mes da primeira escala futura E o mes seguinte --
    // mesma logica (e mesmo motivo) do gerador de datas
    // (getPendingWeekdaysThisMonth, no topo do arquivo): perto da virada
    // do mes, pode sobrar so' 1 data no mes corrente enquanto as proximas
    // ja geradas caem todas no mes seguinte -- sem essa janela dupla, o
    // mural/texto ficava preso mostrando so' essa 1 data solta, mesmo
    // com um monte de escala real logo depois (achado do Drigo, visto
    // na Zeladoria em 31/10/2026: so' sobrava o sabado 31/10 em outubro,
    // e novembro inteiro (07/11, 14/11...) ja' criado ficava de fora).
    const [anoRef, mesRefNum] = String(escalasOrdenadas[0].date).slice(0, 7).split("-").map(Number);
    const proximoMes = new Date(Date.UTC(anoRef, mesRefNum - 1 + 1, 1));
    const chaveMesRef = `${anoRef}-${String(mesRefNum).padStart(2, "0")}`;
    const chaveProximoMes = `${proximoMes.getUTCFullYear()}-${String(proximoMes.getUTCMonth() + 1).padStart(2, "0")}`;
    return escalasOrdenadas.filter((e) => {
      const chave = String(e.date).slice(0, 7);
      return chave === chaveMesRef || chave === chaveProximoMes;
    });
  }

  // Copia as proximas escalas ja' criadas (nao mais "do mes corrente" --
  // `escalas` ja' vem so' com hoje em diante, ver loadEscalas) formatada
  // pra WhatsApp.
  async function handleCopyMonthSchedule() {
    setFeedback("");
    setNotice("");
    setInfo("");
    const escalasOrdenadas = escalas
      .slice()
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const proximasEscalas = getEscalasDoProximoMes(escalasOrdenadas);

    if (proximasEscalas.length === 0) {
      setInfo("Ainda não tem nenhuma escala criada pros próximos cultos.");
      return;
    }

    // Mesma correcao do titulo fixo "Escala de Músicos" feita na imagem
    // (drawScheduleMural) -- o texto pro WhatsApp tinha o mesmo bug.
    const texto = buildMonthScheduleText(proximasEscalas, `Escala de ${TIPO_LABEL[tipo] || "músicos"}`);
    setCopyingSchedule(true);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(texto);
      } else {
        // Fallback pra navegadores/contextos sem Clipboard API (ex.: http
        // nao seguro) -- textarea invisivel + document.execCommand.
        const textarea = document.createElement("textarea");
        textarea.value = texto;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setNotice("Escala do mês copiada! É só colar no WhatsApp.");
    } catch (_) {
      setFeedback("Não consegui copiar automaticamente -- tenta de novo.");
    } finally {
      setCopyingSchedule(false);
    }
  }

  // Mesma logica do "copiar proximas escalas" (ver getEscalasDoProximoMes
  // acima) -- pega todas as escalas do mes da primeira data futura,
  // venham do mes que vierem, sem numero fixo que corta o ultimo domingo
  // em mes de 5 ocorrencias (achado do Drigo, 30/10/2026).
  // soProxima=true: so' a PROXIMA escala (1 data), util em ministerio com
  // muita gente por data (Zeladoria em grupos, Louvor) onde a imagem do
  // mes inteiro fica longa demais pra mandar so' o lembrete de uma semana
  // -- pedido do Drigo, 30/10/2026. soProxima=false (padrao): imagem do
  // mes inteiro, comportamento de sempre.
  function handleOpenMural(soProxima = false) {
    setFeedback("");
    setNotice("");
    setInfo("");
    const escalasOrdenadas = escalas
      .slice()
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    const proximasEscalas = soProxima ? escalasOrdenadas.slice(0, 1) : getEscalasDoProximoMes(escalasOrdenadas);

    if (proximasEscalas.length === 0) {
      setInfo("Ainda não tem nenhuma escala criada pros próximos cultos.");
      return;
    }

    let tituloMural;
    if (soProxima) {
      // Mesmo formato "dd/mm · Dia-da-semana · rotulo" usado no cabecalho
      // de cada card dentro do mural (layoutScheduleMural), pra ficar
      // consistente -- so' que aqui vira o titulo do modal inteiro, ja'
      // que tem uma unica data.
      const escala = proximasEscalas[0];
      const [y, m, d] = String(escala.date).slice(0, 10).split("-");
      const weekday = WEEKDAYS_PT[new Date(Date.UTC(Number(y), Number(m) - 1, Number(d))).getUTCDay()];
      tituloMural = `${d}/${m} · ${weekday}${escala.label ? ` · ${escala.label}` : ""}`;
    } else {
      // Titulo do mural: um unico mes quando todas as datas caem nele, ou um
      // intervalo ("Novembro de 2026 a Dezembro de 2026") quando atravessa a
      // virada do mes -- mesmo raciocinio do titulo do texto copiado.
      const [anoIni, mesIni] = String(proximasEscalas[0].date).slice(0, 7).split("-");
      const [anoFim, mesFim] = String(proximasEscalas[proximasEscalas.length - 1].date).slice(0, 7).split("-");
      tituloMural =
        anoIni === anoFim && mesIni === mesFim
          ? `${MONTHS_PT[Number(mesIni) - 1]} de ${anoIni}`
          : `${MONTHS_PT[Number(mesIni) - 1]} de ${anoIni} a ${MONTHS_PT[Number(mesFim) - 1]} de ${anoFim}`;
    }

    setMuralEscalas(proximasEscalas);
    setMuralTitulo(tituloMural);
    setMuralReady(false);
    setMuralOpen(true);
  }

  function handleDownloadMural() {
    const canvas = muralCanvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `escala-${slugifyMuralTitulo(muralTitulo)}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  // Compartilhamento nativo (Web Share API com arquivo) -- abre direto o
  // seletor de apps do celular (WhatsApp entre eles) com a imagem ja'
  // anexada. Nem todo navegador/contexto suporta -- cai pro download
  // simples quando nao suporta (ex.: a maioria dos navegadores de desktop).
  //
  // So' manda `files` pro navigator.share -- sem `title`/`text` junto.
  // Bug encontrado pelo Drigo em 31/10/2026 no Safari/macOS: escolhendo
  // "Copiar" na folha de compartilhamento nativa (com arquivo + texto
  // juntos) e colando no WhatsApp Web, a imagem colava duplicada. Share
  // de arquivo + texto ao mesmo tempo e' uma combinacao conhecida por
  // gerar multiplos itens na area de transferencia (um pro arquivo, outro
  // pro texto) que apps como WhatsApp as vezes colam como 2 anexos em vez
  // de 1 imagem com legenda -- mandando so' o arquivo evita o problema.
  async function handleShareMural() {
    const canvas = muralCanvasRef.current;
    if (!canvas) return;
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `escala-${slugifyMuralTitulo(muralTitulo)}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
        } catch (_) {
          // compartilhamento cancelado pela pessoa -- nao e' erro, so' ignora
        }
      } else {
        handleDownloadMural();
        setNotice("Esse navegador não abre o compartilhamento direto -- a imagem foi baixada, é só anexar no WhatsApp.");
      }
    }, "image/png");
  }

  async function handleAddSlot(escalaId) {
    const funcao = slotFuncao.trim();
    if (!funcao) return;
    setSavingSlot(true);
    try {
      await apiPost(`/escalas/${escalaId}/slots`, { body: { funcao } });
      setSlotFuncao("");
      setSlotFormOpenFor(null);
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível adicionar a vaga.");
    } finally {
      setSavingSlot(false);
    }
  }

  async function handleAssign(musicianPersonId) {
    if (!assignTarget) return;
    setAssigning(true);
    try {
      await apiPost(`/escalas/slots/${assignTarget.slotId}/assign`, { body: { musicianPersonId } });
      setAssignTarget(null);
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível escalar.");
    } finally {
      setAssigning(false);
    }
  }

  async function handleAddSong(songId) {
    if (!addSongTarget) return;
    setAddingSong(true);
    try {
      await apiPost(`/escalas/${addSongTarget}/songs`, { body: { songId } });
      setAddSongTarget(null);
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível linkar a música.");
    } finally {
      setAddingSong(false);
    }
  }

  async function handleDeleteSong() {
    if (!deleteSongTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/escalas/songs/${deleteSongTarget.id}`);
      setDeleteSongTarget(null);
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível remover a música da escala.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleAddIndisponivel(personId) {
    if (!addIndisponivelTarget) return;
    setAddingIndisponivel(true);
    try {
      await apiPost(`/escalas/${addIndisponivelTarget}/indisponiveis`, { body: { personId } });
      setAddIndisponivelTarget(null);
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível marcar como indisponível.");
    } finally {
      setAddingIndisponivel(false);
    }
  }

  // Auto-servico pra quem nao e' ADMIN (ver escalaGate em escalas.js, que
  // so' deixa o musico mexer na propria indisponibilidade): marca direto a
  // propria Person, sem abrir o picker de "qualquer pessoa" que so' ADMIN
  // deveria ver.
  async function handleMarkSelfIndisponivel(escalaId) {
    if (!ownPersonId) return;
    setAddingIndisponivel(true);
    setFeedback("");
    try {
      await apiPost(`/escalas/${escalaId}/indisponiveis`, { body: { personId: ownPersonId } });
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível marcar como indisponível.");
    } finally {
      setAddingIndisponivel(false);
    }
  }

  async function handleDeleteIndisponivel() {
    if (!deleteIndisponivelTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/escalas/indisponiveis/${deleteIndisponivelTarget.id}`);
      setDeleteIndisponivelTarget(null);
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível desmarcar.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleDeleteEscala() {
    if (!deleteEscalaTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/escalas/${deleteEscalaTarget.id}`);
      setDeleteEscalaTarget(null);
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível excluir a escala.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleDeleteSlot() {
    if (!deleteSlotTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/escalas/slots/${deleteSlotTarget.id}`);
      setDeleteSlotTarget(null);
      await loadEscalas();
    } catch (err) {
      setFeedback(err?.error || "Não foi possível excluir a vaga.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div style={pageStyle}>
      {/* Alargado de 720 pra 960 (30/10/2026, pedido da Carla olhando a tela
          num monitor largo) -- mesmo espirito do ajuste ja feito em
          /admin/usuarios (880 -> 1200): cards e listas continuam com seus
          proprios limites internos (busca de coordenador, modais), so' o
          contêiner da pagina ganhou mais fôlego. */}
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Painel administrativo
        </Link>

        {/* ADMIN ve o seletor completo (10 tipos); quem nao e' ADMIN so' ve
            as abas da propria Person (accessibleTipos, que desde 29/10/2026
            inclui tambem os tipos coordenados, nao so' os de ministerio) --
            e so' aparece o seletor quando ha mais de 1 (pessoa so' com 1
            tipo nem precisa escolher, fica direto na unica aba dela). Usa
            `isAdminUser` aqui (nao `canEdit`, que agora e' por aba) --
            ve-todas-as-abas e' privilegio de ADMIN de verdade, nao de
            coordenador (que so' tem a(s) aba(s) dele). */}
        {(isAdminUser ? TIPO_OPTIONS : TIPO_OPTIONS.filter((opt) => accessibleTipos.includes(opt.value))).length > (isAdminUser ? 0 : 1) && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
            {(isAdminUser ? TIPO_OPTIONS : TIPO_OPTIONS.filter((opt) => accessibleTipos.includes(opt.value))).map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  if (opt.value === tipo) return;
                  setOnlyMine(false);
                  setTipo(opt.value);
                }}
                style={{
                  ...secondaryButtonStyle,
                  padding: "6px 14px",
                  fontSize: 13,
                  ...(opt.value === tipo
                    ? { background: "rgba(96,165,250,0.18)", border: "1px solid rgba(96,165,250,0.5)", color: "#93C5FD" }
                    : {})
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, display: "flex", alignItems: "center", gap: 10 }}>
            <CalendarCheck2 size={22} /> Escala de {TIPO_LABEL[tipo]}
          </h1>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {canEdit && (
              <button
                onClick={handleGenerateSundays}
                disabled={generatingSundays || pendingSundaysThisMonth.length === 0}
                title={pendingSundaysThisMonth.length === 0 ? `Todos os próximos ${weeklyGeneratorConfig.plural} já têm escala criada.` : undefined}
                style={{ ...secondaryButtonStyle, display: "inline-flex", alignItems: "center", gap: 6, opacity: (!generatingSundays && pendingSundaysThisMonth.length === 0) ? 0.5 : 1, cursor: (!generatingSundays && pendingSundaysThisMonth.length === 0) ? "not-allowed" : "pointer" }}
              >
                <CalendarPlus size={16} />
                {generatingSundays
                  ? "Gerando…"
                  : pendingSundaysThisMonth.length === 0
                  ? `Próximos ${weeklyGeneratorConfig.plural} já gerados`
                  : `Gerar próximos ${weeklyGeneratorConfig.plural}`}
              </button>
            )}
            {ownPersonId && (
              <button
                onClick={() => setOnlyMine((v) => !v)}
                style={{
                  ...secondaryButtonStyle,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  ...(onlyMine ? { background: "rgba(96,165,250,0.18)", border: "1px solid rgba(96,165,250,0.5)", color: "#93C5FD" } : {})
                }}
              >
                <User size={16} /> {onlyMine ? "Vendo só as minhas" : "Minhas escalas"}
              </button>
            )}
            <button
              onClick={handleCopyMonthSchedule}
              disabled={copyingSchedule}
              style={{ ...secondaryButtonStyle, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Copy size={16} /> {copyingSchedule ? "Copiando…" : "Copiar próximas escalas"}
            </button>
            <button
              onClick={() => handleOpenMural(true)}
              style={{ ...secondaryButtonStyle, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <ImagePlus size={16} /> Imagem da próxima
            </button>
            <button
              onClick={() => handleOpenMural(false)}
              style={{ ...secondaryButtonStyle, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <ImagePlus size={16} /> Gerar imagem do mês
            </button>
            {canEdit && (
              <button
                onClick={() => {
                  setForm(EMPTY_ESCALA_FORM);
                  setFormError("");
                  setModeloEscalaId("");
                  setCopyAssigned(false);
                  setFormOpen(true);
                  loadModeloOptions();
                }}
                style={{ ...primaryButtonStyle, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Plus size={16} /> Nova escala
              </button>
            )}
          </div>
        </div>

        <p style={{ margin: "0 0 20px", fontSize: 13, color: "#8d98ab" }}>
          {canEdit
            ? 'Toque numa vaga pra escolher quem vai estar na escala. O aviso por WhatsApp pra quem for escalado ainda depende de um template aprovado pela Meta (pedido em paralelo) — até lá, a escalação funciona normalmente, só o aviso automático que não sai.'
            : "Essa é a escala confirmada pra cada culto. Só quem é admin ou coordenador desse ministério pode editar."}
        </p>

        {/* Coordenador(es) desta escala -- so' ADMIN gerencia quem coordena
            cada tipo (decidir QUEM e' coordenador continua privilegio de
            admin; o coordenador em si so' ganha o direito de editar a
            escala do proprio tipo, ver canEdit acima). Pedido do Drigo,
            29/10/2026: "perfil admin pra ser selecionado o nome em cada
            tipo de escala, que possam editar aquela escala". */}
        {isAdminUser && (
          <div style={{ ...cardStyle, padding: 14, marginBottom: 20 }}>
            <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 800, color: "#c7ced9" }}>
              Coordenador(es) de {TIPO_LABEL[tipo]}
            </p>
            <p style={{ margin: "0 0 10px", fontSize: 12, color: "#8d98ab" }}>
              Quem estiver aqui pode editar a escala de {TIPO_LABEL[tipo]} (criar/editar data, vagas, escalar, gerar
              próximos {weeklyGeneratorConfig.plural}) sem precisar ser admin geral — e sem precisar fazer parte do
              ministério.
            </p>
            {coordenadores.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                {coordenadores.map((c) => (
                  <span
                    key={c.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: "rgba(96,165,250,0.14)",
                      border: "1px solid rgba(96,165,250,0.4)",
                      color: "#93C5FD",
                      fontSize: 12.5,
                      fontWeight: 700
                    }}
                  >
                    {c.person?.fullName || "Pessoa removida"}
                    <button
                      type="button"
                      onClick={() => handleRemoveCoordenador(c)}
                      disabled={coordBusyId === c.id}
                      aria-label={`Remover ${c.person?.fullName || "coordenador"}`}
                      style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", color: "inherit", cursor: coordBusyId === c.id ? "not-allowed" : "pointer", padding: 0, opacity: coordBusyId === c.id ? 0.5 : 1 }}
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ position: "relative", maxWidth: 360 }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: 12, color: "#67728a" }} />
              <input
                value={coordSearch}
                onChange={(e) => setCoordSearch(e.target.value)}
                placeholder="Buscar nome pra adicionar como coordenador…"
                style={{ ...inputStyle, paddingLeft: 32 }}
              />
              {coordSearch.trim().length >= 2 && (
                <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
                  {coordSearching && <p style={{ margin: 0, fontSize: 12, color: "#67728a" }}>Buscando…</p>}
                  {!coordSearching && coordSearchResults.length === 0 && (
                    <p style={{ margin: 0, fontSize: 12, color: "#67728a", fontStyle: "italic" }}>Nenhuma pessoa encontrada.</p>
                  )}
                  {coordSearchResults
                    .filter((p) => !coordenadores.some((c) => c.personId === p.id))
                    .map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handleAddCoordenador(p.id)}
                        disabled={coordBusyId === p.id}
                        style={{ ...slotChipStyle(false), justifyContent: "flex-start", opacity: coordBusyId === p.id ? 0.6 : 1 }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <User size={14} /> {p.fullName}
                        </span>
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {feedback && (
          <div style={{ ...cardStyle, padding: 12, marginBottom: 16, border: "1px solid rgba(248,113,113,0.35)", color: "#FCA5A5", fontSize: 13 }}>
            {feedback}
          </div>
        )}

        {notice && (
          <div style={{ ...cardStyle, padding: 12, marginBottom: 16, border: "1px solid rgba(74,222,128,0.35)", color: "#86EFAC", fontSize: 13 }}>
            {notice}
          </div>
        )}

        {info && (
          <div style={{ ...cardStyle, padding: 12, marginBottom: 16, border: "1px solid rgba(148,163,184,0.35)", color: "#CBD5E1", fontSize: 13 }}>
            {info}
          </div>
        )}

        {!loading && visibleEscalas.length > 0 && (
          pendingSummary.totalOpenVagas > 0 ? (
            <div
              style={{
                ...cardStyle,
                padding: 14,
                marginBottom: 16,
                border: "1px solid rgba(252,211,77,0.4)",
                background: "linear-gradient(160deg, rgba(120,95,20,0.18), #131f34)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: "#FDE68A", display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={16} />
                {pendingSummary.totalOpenVagas} vaga{pendingSummary.totalOpenVagas > 1 ? "s" : ""} aberta{pendingSummary.totalOpenVagas > 1 ? "s" : ""} em {pendingSummary.escalasComVaga} data{pendingSummary.escalasComVaga > 1 ? "s" : ""}
              </p>
              <button
                type="button"
                onClick={handleScrollToFirstOpenVaga}
                style={{ ...secondaryButtonStyle, fontSize: 12.5, fontWeight: 700, borderColor: "rgba(252,211,77,0.45)", color: "#FDE68A" }}
              >
                Ver agora
              </button>
            </div>
          ) : (
            <div
              style={{
                ...cardStyle,
                padding: 14,
                marginBottom: 16,
                border: "1px solid rgba(74,222,128,0.3)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <CheckCircle2 size={16} color="#4ADE80" />
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: "#86EFAC" }}>
                Tudo em dia — nenhuma vaga aberta por enquanto.
              </p>
            </div>
          )
        )}

        {loading && <p style={{ color: "#8d98ab", fontSize: 13 }}>Carregando escalas…</p>}

        {!loading && escalas.length === 0 && (
          <div style={{ ...cardStyle, textAlign: "center", padding: 28 }}>
            <p style={{ margin: 0, color: "#8d98ab", fontSize: 14 }}>Nenhuma escala criada ainda pros próximos cultos.</p>
          </div>
        )}

        {!loading && escalas.length > 0 && visibleEscalas.length === 0 && (
          <div style={{ ...cardStyle, textAlign: "center", padding: 28 }}>
            <p style={{ margin: 0, color: "#8d98ab", fontSize: 14 }}>Você não está escalado em nenhuma data por enquanto.</p>
          </div>
        )}

        <div style={{ display: "grid", gap: 16 }}>
          {visibleEscalas.map((escala) => {
            const escalaStats = getEscalaStats(escala);
            const cardStyleComEstado =
              escalaStats.open > 0
                ? {
                    ...cardStyle,
                    border: "1px solid rgba(252,211,77,0.45)",
                    background: "linear-gradient(160deg, rgba(120,95,20,0.16), #131f34)",
                  }
                : cardStyle;
            return (
            <div key={escala.id} id={`escala-${escala.id}`} style={cardStyleComEstado}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  {editDateTarget === escala.id ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <input
                        type="date"
                        value={editDateValue}
                        onChange={(event) => setEditDateValue(event.target.value)}
                        // colorScheme "dark" -- faltava aqui (so' o form de
                        // "Nova escala" tinha), por isso o calendario nativo
                        // abria no tema claro do sistema so' neste campo
                        // (reportado pelo Drigo, 29/10/2026: "o layout do
                        // data picker n esta padronizado"). Mesmo padrao do
                        // resto do site.
                        style={{ ...inputStyle, width: "auto", colorScheme: "dark" }}
                        autoFocus
                      />
                      <input
                        type="text"
                        value={editLabelValue}
                        onChange={(event) => setEditLabelValue(event.target.value)}
                        placeholder="Tema da aula (opcional)"
                        style={{ ...inputStyle, flex: "1 1 220px", minWidth: 180 }}
                      />
                      <button
                        type="button"
                        onClick={() => saveEditDate(escala.id)}
                        disabled={savingDate}
                        style={{ ...iconButtonStyle, borderColor: "rgba(74,222,128,0.4)", color: "#4ADE80" }}
                        aria-label="Salvar data e tema"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditDate}
                        disabled={savingDate}
                        style={iconButtonStyle}
                        aria-label="Cancelar edição"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 900, display: "flex", alignItems: "center", gap: 8 }}>
                      {formatDate(escala.date)}
                      {escala.label ? ` · ${escala.label}` : ""}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => startEditDate(escala)}
                          style={{ ...iconButtonStyle, width: 26, height: 26 }}
                          aria-label="Editar data da escala"
                          title="Editar data (ex.: essa semana cai em outro dia)"
                        >
                          <Pencil size={12} />
                        </button>
                      )}
                    </p>
                  )}
                  {escala.slots.length > 0 && (
                    <p style={{ margin: "4px 0 0", fontSize: 12, fontWeight: 800, color: escalaStats.open > 0 ? "#FCD34D" : "#4ADE80", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span>
                        {escalaStats.filled}/{escalaStats.total} escalados
                        {escalaStats.indisponivelCount > 0 ? ` · ${escalaStats.indisponivelCount} indisponível${escalaStats.indisponivelCount > 1 ? "eis" : ""}` : ""}
                      </span>
                      {escalaStats.open > 0 && (
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            color: "#2a1f06",
                            background: "#FCD34D",
                            borderRadius: 999,
                            padding: "2px 8px",
                            letterSpacing: 0.2,
                          }}
                        >
                          {escalaStats.open} vaga{escalaStats.open > 1 ? "s" : ""} aberta{escalaStats.open > 1 ? "s" : ""}
                        </span>
                      )}
                    </p>
                  )}
                </div>
                {canEdit && (
                  <button
                    onClick={() => setDeleteEscalaTarget(escala)}
                    style={{ ...iconButtonStyle, borderColor: "rgba(248,113,113,0.35)", color: "#FCA5A5" }}
                    aria-label="Excluir escala"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
                <p style={sectionLabelStyle}>Escala</p>
                {escala.slots.length === 0 && (
                  <p style={{ margin: 0, fontSize: 12.5, color: "#67728a", fontStyle: "italic" }}>Nenhuma vaga ainda.</p>
                )}
                {escala.slots.map((slot) => (
                  <div key={slot.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {/* musicianPerson e' o caminho normal (ver escalas.js); musician
                        (User) so aparece em vagas escaladas antes de 17/10/2026. */}
                    {(() => {
                      const musicianName = slot.musicianPerson?.fullName || slot.musician?.name || null;
                      const Tag = canEdit ? "button" : "div";
                      return (
                        <Tag
                          onClick={canEdit ? () => {
                            setPickerSearch("");
                            setAssignTarget({ slotId: slot.id, escalaId: escala.id, funcao: slot.funcao });
                          } : undefined}
                          style={{ ...slotChipStyle(Boolean(musicianName)), flex: 1, cursor: canEdit ? "pointer" : "default" }}
                        >
                          <span>
                            <strong>{slot.funcao}</strong>
                            {" — "}
                            {musicianName || (canEdit ? "Vago, toque pra escalar" : "Vago")}
                          </span>
                          {musicianName && (
                            slot.notifiedAt ? (
                              <CheckCircle2 size={15} color="#4ADE80" aria-label="Avisado por WhatsApp" title="Avisado por WhatsApp" />
                            ) : slot.notifyError && !notifyErrorEhSistemico(slot.notifyError) ? (
                              <NotifyAlertIcon message={slot.notifyError} />
                            ) : null
                          )}
                        </Tag>
                      );
                    })()}
                    {canEdit && (
                      <button
                        onClick={() => setDeleteSlotTarget(slot)}
                        style={{ ...iconButtonStyle, width: 36, height: 36 }}
                        aria-label="Remover vaga"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {canEdit && (slotFormOpenFor === escala.id ? (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <input
                    autoFocus
                    value={slotFuncao}
                    onChange={(e) => setSlotFuncao(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddSlot(escala.id);
                      if (e.key === "Escape") setSlotFormOpenFor(null);
                    }}
                    placeholder="Ex.: Violão, Bateria, Vocal 1…"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button onClick={() => handleAddSlot(escala.id)} disabled={savingSlot} style={primaryButtonStyle}>
                    {savingSlot ? "..." : "Add"}
                  </button>
                  <button onClick={() => setSlotFormOpenFor(null)} style={secondaryButtonStyle}>
                    Cancelar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setSlotFuncao("");
                    setSlotFormOpenFor(escala.id);
                  }}
                  style={{ ...secondaryButtonStyle, marginTop: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
                >
                  <Plus size={14} /> Vaga
                </button>
              ))}

              {tipo === "MUSICOS" && (
              <div style={{ display: "grid", gap: 6, marginTop: 14 }}>
                <p style={sectionLabelStyle}>Repertório</p>
                {(escala.songs || []).length === 0 && (
                  <p style={{ margin: 0, fontSize: 12.5, color: "#67728a", fontStyle: "italic" }}>
                    Nenhuma música do repertório linkada ainda.
                  </p>
                )}
                {(escala.songs || []).map((es) => (
                  <div key={es.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ ...slotChipStyle(true), flex: 1, cursor: "default" }}>
                      <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 8 }}>
                        <Music2 size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                        <span style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                          <span>{es.song?.title}</span>
                          {(es.song?.artist || es.key || es.song?.key) && (
                            <span style={{ fontSize: 11.5, fontWeight: 600, color: "#9fb0cc" }}>
                              {es.song?.artist}
                              {es.song?.artist && (es.key || es.song?.key) ? " · " : ""}
                              {es.key || es.song?.key}
                            </span>
                          )}
                          {(es.song?.referenceUrl || es.song?.lyricsUrl) && (
                            <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                              {es.song?.referenceUrl && (
                                <a
                                  href={es.song.referenceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ ...songLinkStyle, color: "#7DD3FC", borderColor: "rgba(125,211,252,0.35)" }}
                                >
                                  <ExternalLink size={11} /> Ouvir
                                </a>
                              )}
                              {es.song?.lyricsUrl && (
                                <a
                                  href={cifraUrlComTom(es.song.lyricsUrl, es.key || es.song?.key)}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ ...songLinkStyle, color: "#c7ced9" }}
                                >
                                  <FileText size={11} /> Cifra/letra
                                </a>
                              )}
                            </span>
                          )}
                        </span>
                      </span>
                    </div>
                    {canEdit && (
                      <button
                        onClick={() => setDeleteSongTarget({ id: es.id, escalaId: escala.id, title: es.song?.title })}
                        style={{ ...iconButtonStyle, width: 36, height: 36 }}
                        aria-label="Remover música da escala"
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button
                    onClick={() => {
                      setPickerSearch("");
                      setAddSongTarget(escala.id);
                    }}
                    style={{ ...secondaryButtonStyle, justifySelf: "start", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}
                  >
                    <Plus size={13} /> Música
                  </button>
                )}
              </div>
              )}

              <div style={{ display: "grid", gap: 6, marginTop: 14 }}>
                <p style={sectionLabelStyle}>Indisponíveis essa semana</p>
                {(escala.indisponiveis || []).length === 0 && (
                  <p style={{ margin: 0, fontSize: 12.5, color: "#67728a", fontStyle: "italic" }}>Ninguém marcado ainda.</p>
                )}
                {(escala.indisponiveis || []).map((ind) => {
                  const avisoTexto = textoAvisoIndisponivel(escala.date, ind.createdAt);
                  return (
                    <div key={ind.id} style={{ display: "grid", gap: 2 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div
                          style={{
                            ...slotChipStyle(false),
                            flex: 1,
                            cursor: "default",
                            opacity: 0.9,
                            borderStyle: "solid",
                            borderColor: "rgba(251,191,36,0.35)",
                            color: "#FCD34D"
                          }}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <UserX size={14} /> {ind.person?.fullName}
                          </span>
                        </div>
                        {(canEdit || ind.personId === ownPersonId) && (
                          <button
                            onClick={() => setDeleteIndisponivelTarget({ id: ind.id, escalaId: escala.id, fullName: ind.person?.fullName })}
                            style={{ ...iconButtonStyle, width: 36, height: 36 }}
                            aria-label="Desmarcar indisponível"
                          >
                            <X size={15} />
                          </button>
                        )}
                      </div>
                      {avisoTexto && (
                        <span
                          style={{
                            margin: "0 0 0 4px",
                            justifySelf: "start",
                            fontSize: 10.5,
                            fontWeight: 800,
                            color: "#FDE68A",
                            background: "rgba(251,191,36,0.16)",
                            border: "1px solid rgba(251,191,36,0.35)",
                            borderRadius: 999,
                            padding: "2px 8px",
                            letterSpacing: 0.2,
                          }}
                        >
                          {avisoTexto}
                        </span>
                      )}
                    </div>
                  );
                })}
                {canEdit && (
                  <button
                    onClick={() => {
                      setPickerSearch("");
                      setAddIndisponivelTarget(escala.id);
                    }}
                    style={{ ...secondaryButtonStyle, justifySelf: "start", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}
                  >
                    <Plus size={13} /> Indisponível
                  </button>
                )}
                {/* Auto-servico pra MUSICO (nao-ADMIN): so' marca a propria
                    indisponibilidade, sem o picker de "qualquer pessoa" --
                    ver handleMarkSelfIndisponivel e escalaGate em escalas.js.
                    So' aparece se ainda nao estiver marcado nessa escala. */}
                {!canEdit && ownPersonId && !(escala.indisponiveis || []).some((ind) => ind.personId === ownPersonId) && (
                  <button
                    onClick={() => handleMarkSelfIndisponivel(escala.id)}
                    disabled={addingIndisponivel}
                    style={{ ...secondaryButtonStyle, justifySelf: "start", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}
                  >
                    <UserX size={13} /> {addingIndisponivel ? "Marcando…" : (tipo === "MUSICOS" ? "Não vou poder tocar" : "Não vou poder participar")}
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Nova escala */}
      {formOpen && (
        <div style={modalBackdropStyle} onClick={() => !saving && setFormOpen(false)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setFormOpen(false)} style={modalCloseButtonStyle} aria-label="Fechar">
              <X size={16} />
            </button>
            <h2 style={{ margin: "0 0 16px", fontSize: 17, fontWeight: 900 }}>Nova escala</h2>
            <form onSubmit={handleCreateEscala}>
              <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Data</label>
                <input
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  // colorScheme "dark" -- sem isso o calendario nativo abre no
                  // tema claro do sistema (ver screenshot do Drigo), destoando
                  // do resto do formulario. Mesmo padrao do resto do site (ver
                  // AdminRepertorio.jsx "Ultima vez tocada", AdminMembros.jsx,
                  // PrototypeApp.js).
                  style={{ ...inputStyle, colorScheme: "dark" }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Rótulo (opcional)</label>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="Ex.: Culto da noite"
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={fieldLabelStyle}>Usar como modelo (opcional)</label>
                <select
                  value={modeloEscalaId}
                  onChange={(e) => {
                    setModeloEscalaId(e.target.value);
                    if (!e.target.value) setCopyAssigned(false);
                  }}
                  disabled={loadingModelos}
                  style={inputStyle}
                >
                  <option value="">
                    {loadingModelos ? "Carregando…" : "Nenhum — começar vazia"}
                  </option>
                  {modeloOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {formatDate(item.date)}
                      {item.label ? ` — ${item.label}` : ""} ({item.slots?.length || 0} vaga
                      {item.slots?.length === 1 ? "" : "s"})
                    </option>
                  ))}
                </select>
                <p style={{ margin: "6px 0 0", fontSize: 11.5, color: "#8d98ab" }}>
                  Copia as mesmas vagas dessa escala (ex.: Violão, Bateria, Vocal 1…), vazias por padrão.
                </p>
                {modeloEscalaId && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12.5, color: "#c7d1e4", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={copyAssigned}
                      onChange={(e) => setCopyAssigned(e.target.checked)}
                      style={{ width: 16, height: 16 }}
                    />
                    Copiar também quem estava escalado
                  </label>
                )}
              </div>
              {formError && <p style={{ margin: "0 0 12px", fontSize: 12.5, color: "#FCA5A5" }}>{formError}</p>}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" onClick={() => setFormOpen(false)} style={secondaryButtonStyle}>
                  Cancelar
                </button>
                <button type="submit" disabled={saving} style={primaryButtonStyle}>
                  {saving ? "Salvando…" : "Criar escala"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Escolher musico (toque simples) */}
      {assignTarget && (
        <div style={modalBackdropStyle} onClick={() => !assigning && setAssignTarget(null)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setAssignTarget(null)} style={modalCloseButtonStyle} aria-label="Fechar">
              <X size={16} />
            </button>
            <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 900 }}>Escalar · {assignTarget.funcao}</h2>
            <p style={{ margin: "0 0 16px", fontSize: 12.5, color: "#8d98ab" }}>Toque num nome pra escalar.</p>
            {musicos.length > 4 && (
              <div style={{ position: "relative", marginBottom: 10 }}>
                <Search size={14} style={{ position: "absolute", left: 10, top: 12, color: "#67728a" }} />
                <input
                  autoFocus
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Buscar nome…"
                  style={{ ...inputStyle, paddingLeft: 32 }}
                />
              </div>
            )}
            <div style={{ display: "grid", gap: 8 }}>
              <button
                onClick={() => handleAssign(null)}
                disabled={assigning}
                style={{ ...slotChipStyle(false), justifyContent: "center" }}
              >
                Deixar vago (desescalar)
              </button>
              {(() => {
                // Quem esta marcado indisponivel NESSA escala fica de fora
                // da lista pra escalar -- bloqueio de verdade, nao so'
                // aviso (pedido do Rodrigo, 17/10/2026).
                const escalaAtual = escalas.find((e) => e.id === assignTarget.escalaId);
                const bloqueadosIds = new Set((escalaAtual?.indisponiveis || []).map((ind) => ind.personId));
                const termo = normalizeNameText(pickerSearch);
                const disponiveis = musicos
                  .filter((m) => !bloqueadosIds.has(m.id))
                  .filter((m) => !termo || normalizeNameText(m.fullName).includes(termo));
                const bloqueados = musicos.filter((m) => bloqueadosIds.has(m.id));
                return (
                  <>
                    {disponiveis.length === 0 && (
                      <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#67728a", fontStyle: "italic" }}>
                        {termo
                          ? "Nenhum nome encontrado pra essa busca."
                          : `Ninguém marcado pra ${TIPO_LABEL[tipo]} ainda (marque ${TIPO_CHECKBOX_LABEL[tipo]} no cadastro da pessoa em Membros).`}
                      </p>
                    )}
                    {disponiveis.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => handleAssign(m.id)}
                        disabled={assigning}
                        style={{ ...slotChipStyle(false), color: "#e5e7eb" }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <User size={14} /> {m.fullName}
                        </span>
                      </button>
                    ))}
                    {bloqueados.length > 0 && (
                      <p style={{ margin: "8px 0 0", fontSize: 11.5, color: "#67728a", fontStyle: "italic" }}>
                        Indisponíveis essa semana (não dá pra escalar): {bloqueados.map((m) => m.fullName).join(", ")}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Adicionar musica do repertorio na escala */}
      {addSongTarget && (
        <div style={modalBackdropStyle} onClick={() => !addingSong && setAddSongTarget(null)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setAddSongTarget(null)} style={modalCloseButtonStyle} aria-label="Fechar">
              <X size={16} />
            </button>
            <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 900 }}>Adicionar música</h2>
            <p style={{ margin: "0 0 16px", fontSize: 12.5, color: "#8d98ab" }}>Toque numa música do repertório pra linkar nessa escala.</p>
            {repertorio.length > 4 && (
              <div style={{ position: "relative", marginBottom: 10 }}>
                <Search size={14} style={{ position: "absolute", left: 10, top: 12, color: "#67728a" }} />
                <input
                  autoFocus
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Buscar música ou artista…"
                  style={{ ...inputStyle, paddingLeft: 32 }}
                />
              </div>
            )}
            <div style={{ display: "grid", gap: 8, maxHeight: 360, overflowY: "auto" }}>
              {(() => {
                const escalaAtual = escalas.find((e) => e.id === addSongTarget);
                const jaLinkadas = new Set((escalaAtual?.songs || []).map((es) => es.songId));
                const termo = normalizeNameText(pickerSearch);
                const disponiveis = repertorio
                  .filter((s) => !jaLinkadas.has(s.id))
                  .filter(
                    (s) =>
                      !termo ||
                      normalizeNameText(s.title).includes(termo) ||
                      normalizeNameText(s.artist || "").includes(termo)
                  );
                if (disponiveis.length === 0) {
                  return (
                    <p style={{ margin: 0, fontSize: 12.5, color: "#67728a", fontStyle: "italic" }}>
                      {termo
                        ? "Nenhuma música encontrada pra essa busca."
                        : repertorio.length === 0
                        ? "Nenhuma música cadastrada no repertório ainda."
                        : "Todas as músicas do repertório já estão linkadas nessa escala."}
                    </p>
                  );
                }
                return disponiveis.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleAddSong(s.id)}
                    disabled={addingSong}
                    style={{ ...slotChipStyle(false), color: "#e5e7eb" }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 8 }}>
                      <Music2 size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                      <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <span>{s.title}</span>
                        {(s.artist || s.key) && (
                          <span style={{ fontSize: 11.5, fontWeight: 600, color: "#9fb0cc" }}>
                            {s.artist}
                            {s.artist && s.key ? " · " : ""}
                            {s.key}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Marcar pessoa como indisponivel nessa escala */}
      {addIndisponivelTarget && (
        <div style={modalBackdropStyle} onClick={() => !addingIndisponivel && setAddIndisponivelTarget(null)}>
          <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setAddIndisponivelTarget(null)} style={modalCloseButtonStyle} aria-label="Fechar">
              <X size={16} />
            </button>
            <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 900 }}>Marcar indisponível</h2>
            <p style={{ margin: "0 0 16px", fontSize: 12.5, color: "#8d98ab" }}>
              Toque num nome pra bloquear essa pessoa de ser escalada nessa data.
            </p>
            {musicos.length > 4 && (
              <div style={{ position: "relative", marginBottom: 10 }}>
                <Search size={14} style={{ position: "absolute", left: 10, top: 12, color: "#67728a" }} />
                <input
                  autoFocus
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Buscar nome…"
                  style={{ ...inputStyle, paddingLeft: 32 }}
                />
              </div>
            )}
            <div style={{ display: "grid", gap: 8, maxHeight: 360, overflowY: "auto" }}>
              {(() => {
                const escalaAtual = escalas.find((e) => e.id === addIndisponivelTarget);
                const jaMarcados = new Set((escalaAtual?.indisponiveis || []).map((ind) => ind.personId));
                const termo = normalizeNameText(pickerSearch);
                const disponiveis = musicos
                  .filter((m) => !jaMarcados.has(m.id))
                  .filter((m) => !termo || normalizeNameText(m.fullName).includes(termo));
                if (disponiveis.length === 0) {
                  return (
                    <p style={{ margin: 0, fontSize: 12.5, color: "#67728a", fontStyle: "italic" }}>
                      {termo
                        ? "Nenhum nome encontrado pra essa busca."
                        : musicos.length === 0
                        ? `Ninguém marcado pra ${TIPO_LABEL[tipo]} ainda (marque ${TIPO_CHECKBOX_LABEL[tipo]} no cadastro da pessoa em Membros).`
                        : "Todo mundo já está marcado como indisponível nessa escala."}
                    </p>
                  );
                }
                return disponiveis.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => handleAddIndisponivel(m.id)}
                    disabled={addingIndisponivel}
                    style={{ ...slotChipStyle(false), color: "#e5e7eb" }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <UserX size={14} /> {m.fullName}
                    </span>
                  </button>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Confirmar exclusao de escala */}
      {deleteEscalaTarget && (
        <div style={modalBackdropStyle} onClick={() => !deleting && setDeleteEscalaTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <p style={{ margin: "0 0 18px", fontSize: 14, lineHeight: 1.55 }}>
              Excluir a escala de <strong>{formatDate(deleteEscalaTarget.date)}</strong>
              {deleteEscalaTarget.label ? ` (${deleteEscalaTarget.label})` : ""}? Todas as vagas dela somem junto.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteEscalaTarget(null)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button onClick={handleDeleteEscala} disabled={deleting} style={dangerButtonStyle}>
                {deleting ? "Excluindo…" : "Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar exclusao de vaga */}
      {deleteSlotTarget && (
        <div style={modalBackdropStyle} onClick={() => !deleting && setDeleteSlotTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <p style={{ margin: "0 0 18px", fontSize: 14, lineHeight: 1.55 }}>
              Remover a vaga <strong>{deleteSlotTarget.funcao}</strong>?
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteSlotTarget(null)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button onClick={handleDeleteSlot} disabled={deleting} style={dangerButtonStyle}>
                {deleting ? "Removendo…" : "Remover"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar exclusao de musica da escala */}
      {deleteSongTarget && (
        <div style={modalBackdropStyle} onClick={() => !deleting && setDeleteSongTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <p style={{ margin: "0 0 18px", fontSize: 14, lineHeight: 1.55 }}>
              Remover <strong>{deleteSongTarget.title}</strong> do repertório dessa escala?
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteSongTarget(null)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button onClick={handleDeleteSong} disabled={deleting} style={dangerButtonStyle}>
                {deleting ? "Removendo…" : "Remover"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar desmarcacao de indisponivel */}
      {deleteIndisponivelTarget && (
        <div style={modalBackdropStyle} onClick={() => !deleting && setDeleteIndisponivelTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <p style={{ margin: "0 0 18px", fontSize: 14, lineHeight: 1.55 }}>
              Desmarcar <strong>{deleteIndisponivelTarget.fullName}</strong> como indisponível nessa escala? Ela volta a poder ser escalada aqui.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteIndisponivelTarget(null)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button onClick={handleDeleteIndisponivel} disabled={deleting} style={dangerButtonStyle}>
                {deleting ? "Removendo…" : "Remover"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gerar imagem (mural) da escala do mes */}
      {muralOpen && (
        <div style={modalBackdropStyle} onClick={() => setMuralOpen(false)}>
          <div style={{ ...modalCardStyle, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setMuralOpen(false)} style={modalCloseButtonStyle} aria-label="Fechar">
              <X size={16} />
            </button>
            <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 900 }}>Imagem da escala · {muralTitulo}</h2>
            <p style={{ margin: "0 0 16px", fontSize: 12.5, color: "#9fb0cc" }}>
              Baixe ou compartilhe direto no WhatsApp (grupo ou status).
            </p>
            <div
              style={{
                borderRadius: 12,
                overflow: "hidden",
                border: "1px solid rgba(120,152,210,0.28)",
                marginBottom: 16,
                maxHeight: "52vh",
                overflowY: "auto",
                background: "#0b1120"
              }}
            >
              <canvas ref={muralCanvasRef} style={{ display: "block", width: "100%", height: "auto" }} />
            </div>
            {!muralReady && (
              <p style={{ margin: "0 0 16px", fontSize: 12.5, color: "#9fb0cc" }}>Gerando imagem…</p>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button onClick={() => setMuralOpen(false)} style={secondaryButtonStyle}>
                Fechar
              </button>
              <button
                onClick={handleDownloadMural}
                disabled={!muralReady}
                style={{ ...secondaryButtonStyle, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Download size={16} /> Baixar imagem
              </button>
              <button
                onClick={handleShareMural}
                disabled={!muralReady}
                style={{ ...primaryButtonStyle, display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <Share2 size={16} /> Compartilhar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
